import { useCallback, useMemo, useState } from "react"
import { FilterWrapper } from "./FilterWrapper"
import { useFilters } from "./useFilters"
import { FilterItem } from "./FilterItem"
import { type LocationItem, useLocationFilter } from "./useLocationFilter"
import { useSearchParams } from "next/navigation"

interface Props {
  excludeRemote?: boolean
  onClickOverride?: () => { hasSubscription: boolean }
  onSelect?: (filterName: string) => void
}

const id = "location"

export const LocationFilter: React.FC<Props> = ({ excludeRemote = false, onSelect, onClickOverride }) => {
  const [search, setSearch] = useState<string>("")
  const { removeAllByIds } = useFilters()
  const { locations: locationsWithRemote, toggleFilter } = useLocationFilter({ search, id })
  const searchParams = useSearchParams()
  const locations = excludeRemote
    ? locationsWithRemote.filter(location => location.name !== "Remote")
    : locationsWithRemote

  const currentParams = useMemo(() => {
    const params: { [key: string]: string } = {}
    searchParams?.forEach((value, key) => {
      params[key] = value
    })
    return params
  }, [searchParams])

  const getActiveLocation = useCallback(() => {
    return {
      name: currentParams.locationName ? currentParams.locationName : "",
      coordinates: currentParams.location ? currentParams.location.split(",").map(Number.parseFloat) : [0, 0],
    }
  }, [currentParams])

  const { name: activeName, coordinates: activeLocation } = getActiveLocation()

  const onSelectItem = (location: LocationItem) => {
    toggleFilter({
      location,
    })
    onSelect?.(location.name)
  }

  const buttonText = useMemo(() => {
    if (!!activeName) {
      return decodeURIComponent(activeName)
    }
    return "Location"
  }, [activeName])

  const onRemoveAll = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation()
      removeAllByIds(["location", "locationName", "locationType"])
    },
    [removeAllByIds]
  )

  return (
    <FilterWrapper
      id={id}
      onClickOverride={onClickOverride}
      onRemoveAll={onRemoveAll}
      buttonText={buttonText}
      search={search}
      filterActive={!!activeName}
      onChangeSearch={e => setSearch(e.target.value)}>
      <div className="mt-3 flex w-full">
        <div className="flex w-full flex-col gap-1">
          {locations?.map((location, index) => {
            return (
              <FilterItem
                key={`${location.name}${index}`}
                name={location.name}
                selected={activeName === location.name}
                onClick={() => onSelectItem(location)}
              />
            )
          })}
        </div>
      </div>
    </FilterWrapper>
  )
}
